import { useMemo } from 'react';
import { GetStaticPropsContext, GetStaticPropsResult } from 'next';

import { Box, Flex } from '@chakra-ui/react';

import { PageCategory } from '~/features/analytics/constants';
import {
  CatalogFilterDefinitionData,
  getCatalogFilterDataFromServer,
} from '~/features/catalog/controllers/get-catalog-filter-data-from-server';
import { getCatalogSearchResults } from '~/features/catalog/controllers/get-catalog-search-results';
import { CatalogSearchResults } from '~/features/catalog/models/catalog-search-result';
import { DEFAULT_SORT, UriFilter } from '~/features/catalog/models/filter';
import { Filters } from '~/features/catalog/models/filter';
import {
  getClaudeEngineerFlagStaticPaths,
  getClaudeEngineerFlagVariantFromParams,
} from '~/features/claude-engineer-2026/controllers/get-claude-engineer-flag-static-paths';
import type { ClaudeEngineerVariant } from '~/features/claude-engineer-2026/models/variant';
import { getClaudeEngineerCatalogCarouselItems } from '~/features/claude-engineer-2026/utils/get-claude-engineer-catalog-carousel-items';
import { Catalog } from '~/features/experiments/ab_plp_redesign_2025/components/catalog';
import {
  CarouselItem,
  CatalogHeroSection,
} from '~/features/experiments/ab_plp_redesign_2025/components/catalog-hero-section';
import {
  CatalogSubcategoriesSection,
  StaticCatalogSubcategoriesSectionProps,
  transformStaticCatalogSubcategoriesSectionProps,
} from '~/features/experiments/ab_plp_redesign_2025/components/catalog-subcategories-section';
import { useExperimentalSegmentEvents } from '~/features/experiments/ab_plp_redesign_2025/hooks';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { useSessionReplay } from '~/features/session-replay';
import { useI18n } from '~/hooks/use-i18n';
import { useScrollDepthEvent } from '~/hooks/use-scroll-depth-event';

type CatalogPageProps = {
  page: Page;
  catalogSearchResults: CatalogSearchResults | null;
  skillUriFilters: UriFilter[];
  schoolUriFilters: UriFilter[];
  staticCatalogSubcategoriesSectionProps: StaticCatalogSubcategoriesSectionProps;
  variant: ClaudeEngineerVariant;
};

export default function CatalogClaudeEngineerFlagPage(props: CatalogPageProps) {
  const { t, isTranslated } = useI18n();
  const experimentalSegmentEvents = useExperimentalSegmentEvents('dino');

  useSessionReplay();

  useScrollDepthEvent({
    threshold: [25, 50, 75, 99],
    analyticsProperties: {
      category: PageCategory.Catalog,
    },
    isEnabled: experimentalSegmentEvents?.variant === 'on',
  });

  const {
    page,
    catalogSearchResults,
    schoolUriFilters,
    skillUriFilters,
    staticCatalogSubcategoriesSectionProps,
    variant,
  } = props;

  const carouselItems: CarouselItem[] = useMemo(() => getClaudeEngineerCatalogCarouselItems(t, variant), [t, variant]);

  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.Catalog} />
      <PageLayout withoutVerticalPadding>
        <Box bgColor='grey.50' paddingBottom={{ base: '56px', md: '112px' }}>
          <CatalogHeroSection itemsOverride={carouselItems} summerSaleInsertIndex={0} />
          <Flex
            flexDirection='column'
            paddingTop={{ base: '24px', md: '40px' }}
            gap={{
              base: '56px',
              md: '112px',
            }}
          >
            <Catalog
              initialCatalogSearchResults={catalogSearchResults}
              schoolUriFilters={schoolUriFilters}
              skillUriFilters={skillUriFilters}
            />
            {!isTranslated && <CatalogSubcategoriesSection {...staticCatalogSubcategoriesSectionProps} />}
          </Flex>
        </Box>
      </PageLayout>
    </>
  );
}

export function getStaticPaths() {
  return getClaudeEngineerFlagStaticPaths();
}

export async function getStaticProps({
  locale,
  params,
}: GetStaticPropsContext<{ variant: string }>): Promise<GetStaticPropsResult<CatalogPageProps>> {
  const variantResult = getClaudeEngineerFlagVariantFromParams(params, locale);

  if ('notFound' in variantResult) {
    return variantResult;
  }

  const defaultLocaleMetadata = getCatalogFilterDataFromServer('en-us') as CatalogFilterDefinitionData;
  const skillUriFilters = defaultLocaleMetadata?.skills || [];
  const schoolUriFilters = defaultLocaleMetadata?.schools || [];

  const [page, staticCatalogSubcategoriesSectionProps] = await Promise.all([
    getPage('/catalog'),
    transformStaticCatalogSubcategoriesSectionProps(),
  ]);

  const catalogSearchResults = await getCatalogSearchResults({
    selectedFilters: {} as Filters,
    selectedSort: DEFAULT_SORT,
    page: 0,
    schoolUriFilters,
    skillUriFilters,
  });

  return {
    props: {
      page,
      catalogSearchResults,
      skillUriFilters,
      schoolUriFilters,
      staticCatalogSubcategoriesSectionProps,
      variant: variantResult.variant,
    },
  };
}
